import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Check, X } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { Input } from "@/components/ui/input";

const CATEGORIES = ["supplies", "materials", "equipment", "food", "marketing", "travel", "overhead", "other"];

const EMPTY = (scope, parentKey, parentId) => ({
  name: "",
  category: "supplies",
  scope,
  [parentKey]: parentId,
  cost_type: scope === "booking" ? "per_booking" : "per_class",
  unit_cost: 0,
  case_size: 1,
  quantity_needed: 1,
  notes: "",
});

/** Inline expense editor for a class (scope=class) or booking (scope=booking). */
export default function ExpenseEditor({ scope, parentId, onChange }) {
  const parentKey = scope === "class" ? "class_id" : "booking_id";
  const queryKey = ["expenses", scope, parentId];
  const { data: items = [], refetch } = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get(`/admin/expenses?scope=${scope}&${parentKey}=${parentId}`)).data,
    enabled: !!parentId,
  });

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY(scope, parentKey, parentId));
  const [editingId, setEditingId] = useState(null);

  const refresh = async () => {
    await refetch();
    onChange?.();
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      await api.post("/admin/expenses", {
        ...draft,
        unit_cost: Number(draft.unit_cost) || 0,
        case_size: Math.max(1, Number(draft.case_size) || 1),
        quantity_needed: Number(draft.quantity_needed) || 1,
      });
      toast.success("Expense added");
      setDraft(EMPTY(scope, parentKey, parentId));
      setAdding(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/expenses/${id}`, patch);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this expense item?")) return;
    try {
      await api.delete(`/admin/expenses/${id}`);
      toast.success("Expense removed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="rounded-xl border border-gg-border bg-gg-bg p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">
          💰 Expenses ({scope})
        </p>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-xs px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white transition-colors inline-flex items-center gap-1"
            data-testid="expense-add-btn"
          >
            <Plus size={12} /> Add expense
          </button>
        )}
      </div>

      {items.length === 0 && !adding && (
        <p className="text-xs text-gg-muted italic">No expenses recorded yet.</p>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((e) => (
            <ExpenseRow
              key={e.id}
              expense={e}
              editing={editingId === e.id}
              onEdit={() => setEditingId(e.id)}
              onCancel={() => setEditingId(null)}
              onSave={async (patch) => { await update(e.id, patch); setEditingId(null); }}
              onDelete={() => remove(e.id)}
              scope={scope}
            />
          ))}
        </div>
      )}

      {adding && (
        <div className="mt-3 rounded-lg border-2 border-dashed border-gg-green/40 bg-gg-surface p-3 space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <LabeledInput label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="e.g. Mason jars" testid="expense-name" />
            <LabeledSelect label="Category" value={draft.category} onChange={(v) => setDraft({ ...draft, category: v })} options={CATEGORIES} />
            <LabeledSelect
              label="Cost type"
              value={draft.cost_type}
              onChange={(v) => setDraft({ ...draft, cost_type: v })}
              options={scope === "booking" ? ["per_booking", "one_time"] : ["per_class", "per_student", "one_time"]}
            />
            <LabeledInput label="Unit cost ($)" type="number" step="0.01" value={draft.unit_cost} onChange={(v) => setDraft({ ...draft, unit_cost: v })} testid="expense-unit-cost" />
            <LabeledInput label="Case size (1 = single unit)" type="number" value={draft.case_size} onChange={(v) => setDraft({ ...draft, case_size: v })} hint="e.g. mason jars bought in a case of 12 → put 12" testid="expense-case-size" />
            <LabeledInput label="Quantity needed" type="number" step="0.01" value={draft.quantity_needed} onChange={(v) => setDraft({ ...draft, quantity_needed: v })} testid="expense-qty" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => { setAdding(false); setDraft(EMPTY(scope, parentKey, parentId)); }} className="text-xs px-3 py-1 rounded-full border border-gg-border text-gg-muted hover:text-[var(--gg-text)]">Cancel</button>
            <button type="button" onClick={save} className="text-xs px-3 py-1 rounded-full bg-gg-green text-white hover:opacity-90" data-testid="expense-save">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ExpenseRow({ expense: e, editing, onEdit, onCancel, onSave, onDelete, scope }) {
  const [form, setForm] = useState({
    name: e.name, category: e.category,
    cost_type: e.cost_type, unit_cost: e.unit_cost,
    case_size: e.case_size, quantity_needed: e.quantity_needed,
  });
  const effective = (Number(form.unit_cost) || 0) / Math.max(1, Number(form.case_size) || 1);
  const lineHint = form.cost_type === "per_student"
    ? `${formatPrice(effective)} × N students`
    : `${formatPrice(effective * (Number(form.quantity_needed) || 1))}`;

  if (!editing) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-gg-border bg-gg-surface p-2.5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">{e.name}</span>
            <span className="text-[10px] uppercase tracking-[0.12em] text-gg-brown bg-gg-bg px-1.5 py-0.5 rounded">{e.category}</span>
            <span className="text-[10px] uppercase tracking-[0.12em] text-gg-green bg-gg-green/10 px-1.5 py-0.5 rounded">{e.cost_type}</span>
          </div>
          <div className="text-xs text-gg-muted mt-0.5">
            {formatPrice(e.unit_cost)} {e.case_size > 1 ? `÷ ${e.case_size} (case)` : ""} × {e.quantity_needed} = {formatPrice(e.line_total ?? e.effective_unit_cost * e.quantity_needed)}
          </div>
        </div>
        <button type="button" onClick={onEdit} className="text-gg-brown hover:text-gg-green p-1" data-testid={`expense-edit-${e.id}`}><Edit2 size={14} /></button>
        <button type="button" onClick={onDelete} className="text-gg-muted hover:text-[#B24C4C] p-1" data-testid={`expense-delete-${e.id}`}><Trash2 size={14} /></button>
      </div>
    );
  }
  return (
    <div className="rounded-lg border-2 border-gg-green/40 bg-gg-surface p-3">
      <div className="grid sm:grid-cols-2 gap-2">
        <LabeledInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <LabeledSelect label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} />
        <LabeledSelect label="Cost type" value={form.cost_type} onChange={(v) => setForm({ ...form, cost_type: v })} options={scope === "booking" ? ["per_booking", "one_time"] : ["per_class", "per_student", "one_time"]} />
        <LabeledInput label="Unit cost ($)" type="number" step="0.01" value={form.unit_cost} onChange={(v) => setForm({ ...form, unit_cost: v })} />
        <LabeledInput label="Case size" type="number" value={form.case_size} onChange={(v) => setForm({ ...form, case_size: v })} />
        <LabeledInput label="Quantity needed" type="number" step="0.01" value={form.quantity_needed} onChange={(v) => setForm({ ...form, quantity_needed: v })} />
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <span className="text-gg-muted italic">Effective: {lineHint}</span>
        <div className="flex gap-1">
          <button type="button" onClick={onCancel} className="text-xs px-3 py-1 rounded-full border border-gg-border"><X size={12} /></button>
          <button type="button" onClick={() => onSave({
            name: form.name, category: form.category, cost_type: form.cost_type,
            unit_cost: Number(form.unit_cost) || 0,
            case_size: Math.max(1, Number(form.case_size) || 1),
            quantity_needed: Number(form.quantity_needed) || 1,
          })} className="text-xs px-3 py-1 rounded-full bg-gg-green text-white"><Check size={12} /></button>
        </div>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, type = "text", step, placeholder, hint, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.14em] text-gg-brown font-semibold">{label}</span>
      <Input value={value} type={type} step={step} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 text-sm" data-testid={testid} />
      {hint && <span className="text-[10px] text-gg-muted italic mt-0.5 block">{hint}</span>}
    </label>
  );
}

function LabeledSelect({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.14em] text-gg-brown font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-9 rounded-md border border-gg-border bg-gg-surface px-2 text-sm">
        {options.map((o) => (
          <option key={o} value={o}>{o.replace(/_/g, " ")}</option>
        ))}
      </select>
    </label>
  );
}
